import type { NormalizedRoutes, RouteDescriptor, RouteNode } from "./types";

export type QueryToken = { key: string; token: string; defaultValue?: string };

// A query-discriminated child of a route: `?key=<case>` selects a case, whose
// params ride in the standard bracket form `key[param]=value`. Each case is an
// ordinary "k=:k=default" pattern fragment and becomes a page-agnostic route
// scope (pathname `/*`, discriminant as a literal) usable anywhere a route is.
export function oneOf<const C extends Record<string, string>>(cases: C) {
  return { cases } as { cases: C } & {
    [K in keyof C]: RouteDescriptor<`/*?${C[K]}`>;
  };
}
function isOneOf(v: unknown): v is { cases: Record<string, string> } {
  return Boolean(
    v && typeof v === "object" && "cases" in v && !("pattern" in v)
  );
}
type CaseNode = RouteNode & { discriminant: string; caseName: string };
export function isCaseNode(v: RouteNode): v is CaseNode {
  return "discriminant" in v;
}
function nsToken(key: string, t: QueryToken, token: string) {
  return `${key}[${t.key}]=:${token}${t.defaultValue === undefined ? "" : `=${t.defaultValue}`}`;
}

export function route<P extends string, C>(pattern: P, children?: C) {
  let folded: string = pattern;
  for (const [key, child] of Object.entries(children ?? {})) {
    if (!isOneOf(child)) continue;
    // Fold the union of case params onto this route (so every page carries
    // them), and give each case its own page-agnostic pattern.
    const union = new Map([[key, `${key}=:${key}`]]);
    for (const [caseName, str] of Object.entries(child.cases)) {
      const tokens = extractTokens(`?${str}`);
      for (const t of tokens) {
        union.set(t.key, nsToken(key, t, `${key}[${t.key}]`));
      }
      const query = tokens.map((t) => nsToken(key, t, t.token)).join("&");
      (child as Record<string, unknown>)[caseName] = {
        pattern: `/*?${key}=${caseName}${query && `&${query}`}`,
        discriminant: key,
        caseName,
      } satisfies CaseNode;
    }
    const q = [...union.values()].join("&");
    folded = folded.includes("?") ? `${folded}&${q}` : `${folded}?${q}`;
  }
  return Object.assign(
    { pattern: folded },
    children ?? {}
  ) as RouteDescriptor<P> & C;
}

export function hidden<T extends RouteNode>(node: T): T {
  node.hidden = true;
  return node;
}

export function isRouteNode(value: unknown): value is RouteNode {
  return Boolean(
    value &&
    typeof value === "object" &&
    "pattern" in (value as Record<string, unknown>)
  );
}

export function normalizeRoutes<T extends Record<string, RouteNode>>(
  config: T,
  base = "",
  baseTail = ""
) {
  for (const value of Object.values(config)) {
    normalizeNode(value, base, baseTail);
  }
  return config as unknown as NormalizedRoutes<T>;
}

function normalizeNode(node: RouteNode, base: string, baseTail = "") {
  const { path, tail } = splitPattern(node.pattern);
  const absolute = resolvePath(base, path);
  const mergedTail = mergeTails(baseTail, tail);
  node.pattern = `${absolute}${mergedTail}`;
  node.ownTail = tail;
  for (const value of Object.values(node)) {
    if (isRouteNode(value)) normalizeNode(value, absolute, mergedTail);
  }
}

function mergeTails(parentTail: string, childTail: string): string {
  if (!parentTail) return childTail;
  if (!childTail) return parentTail;

  // Extract query and hash parts
  const parentParts = splitPattern(`/${parentTail}`);
  const childParts = splitPattern(`/${childTail}`);

  // Merge query params
  const parentQuery = parentParts.tail.replace(/^[?#]/, "").split("?")[0] || "";
  const childQuery = childParts.tail.replace(/^[?#]/, "").split("?")[0] || "";

  const mergedParams = new Map<string, string>();

  // Add parent params first
  if (parentQuery) {
    for (const segment of parentQuery.split("&")) {
      const eqIndex = segment.indexOf("=");
      if (eqIndex > 0) {
        mergedParams.set(segment.slice(0, eqIndex), segment.slice(eqIndex + 1));
      }
    }
  }

  // Child params override parent params
  if (childQuery) {
    for (const segment of childQuery.split("&")) {
      const eqIndex = segment.indexOf("=");
      if (eqIndex > 0) {
        mergedParams.set(segment.slice(0, eqIndex), segment.slice(eqIndex + 1));
      }
    }
  }

  if (mergedParams.size === 0) return "";

  const queryString = Array.from(mergedParams.entries())
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

  return queryString ? `?${queryString}` : "";
}

export function extractTokens(part?: string): QueryToken[] {
  if (!part) return [];
  const tokens: QueryToken[] = [];

  for (const segment of part.replace(/^[?#]/, "").split("&")) {
    const eqIndex = segment.indexOf("=");
    if (eqIndex === -1) continue;

    const key = segment.slice(0, eqIndex);
    const value = segment.slice(eqIndex + 1);

    if (!key || key.length === 0 || !value) continue;

    // Parse :token=defaultValue syntax
    const tokenMatch = value.match(/^:([A-Za-z0-9_[\]]+)(?:=(.*))?$/);
    if (!tokenMatch) continue;

    const [, token, defaultValue] = tokenMatch;
    if (!token || token.length === 0) continue;

    tokens.push({ key, token, defaultValue });
  }

  return tokens;
}

function splitPattern(pattern: string) {
  const [pathAndSearch = "", hashPart = ""] = pattern.split("#", 2);
  const [path = "", queryPart = ""] = pathAndSearch.split("?", 2);
  const tail = `${queryPart ? `?${queryPart}` : ""}${
    hashPart ? `#${hashPart}` : ""
  }`;
  return { path, tail };
}

function resolvePath(base: string, segment: string) {
  if (!segment) return base || "/";
  const trimmed = segment.replace(/^\/+/, "");
  if (!trimmed) return base || "/";
  if (!base || base === "/") return `/${trimmed}`;
  return `${base.replace(/\/+$/, "")}/${trimmed}`;
}
